// the validation mirror (same rules as the server) and amounts in words.
import { digitsOnly, jdnToIso, makeRules, passwordParts, type RuleResult } from '../rules.ts';
import { $$, bankMark, esc, icon, reduceMotion } from './core.ts';
import { TODAY } from './date.ts';

/** The rule functions makeRules returns, by name. */
type Rules = ReturnType<typeof makeRules>;
/** One rule as check() calls it: the value, then the extra arguments of money (min, max) or a date (today). */
type RuleFn = (v: unknown, a?: unknown, b?: unknown) => RuleResult;
/** How a field's message box shows: an error, a confirmation, or its own help text (null). */
type MsgKind = 'err' | 'ok' | null;

// ---------------- validation mirror ----------------
export let rules: Rules | null = null,
  messages: Record<string, string> = {};
const RULE: Record<string, keyof Rules> = {
  money: 'money',
  card: 'card',
  'card-last4': 'cardLast4',
  iban: 'iban',
  'national-code': 'nationalCode',
  mobile: 'mobile',
  postal: 'postalCode',
  'jalali-date': 'jalaliDate',
  time: 'time',
  reference: 'reference',
  percent: 'percent',
  email: 'email',
  password: 'password',
};
const FORMAT: Record<string, ((v: string) => string) | undefined> = {
  money: v => {
    const d = digitsOnly(v).replace(/^0+(?=\d)/, '');
    return d ? Number(d).toLocaleString('en-US') : '';
  },
  card: v =>
    digitsOnly(v)
      .slice(0, 16)
      .replace(/(.{4})(?=.)/g, '$1 '),
  iban: v =>
    digitsOnly(v.replace(/^IR/i, ''))
      .slice(0, 24)
      .replace(/(.{4})(?=.)/g, '$1 '),
  'card-last4': v => digitsOnly(v).slice(0, 4),
  'national-code': v => digitsOnly(v).slice(0, 10),
  postal: v => digitsOnly(v).slice(0, 10),
  reference: v => digitsOnly(v).slice(0, 20),
  'jalali-date': v => {
    const d = digitsOnly(v).slice(0, 8);
    return d.length > 6
      ? d.slice(0, 4) + '/' + d.slice(4, 6) + '/' + d.slice(6)
      : d.length > 4
        ? d.slice(0, 4) + '/' + d.slice(4)
        : d;
  },
  time: v => {
    const d = digitsOnly(v).slice(0, 4);
    return d.length > 2 ? d.slice(0, 2) + ':' + d.slice(2) : d;
  },
};
const BANK_OK: Record<string, string> = { card: 'کارت بانک ', iban: 'حساب بانک ' };
export async function loadRules(): Promise<void> {
  if (rules) return;
  const [b, m] = (await Promise.all([
    fetch('/ds/banks.json').then(r => r.json()),
    fetch('/ds/rules.fa.json').then(r => r.json()),
  ])) as [NonNullable<Window['dsBanks']>, Record<string, string>];
  rules = makeRules(b);
  messages = m;
  window.dsBanks = b;
}
function fieldOf(input: HTMLInputElement): HTMLElement | null {
  return input.closest('.ds-field');
}
function setMsg(field: HTMLElement, kind: MsgKind, html?: string | null): void {
  const m = field.querySelector<HTMLElement>('.ds-msg');
  if (!m) return;
  if (!m.dataset.info) m.dataset.info = m.innerHTML;
  m.innerHTML = html ?? m.dataset.info;
  field.classList.toggle('is-invalid', kind === 'err');
  field.classList.toggle('is-valid', kind === 'ok');
}
function check(input: HTMLInputElement, show: boolean): boolean {
  const field = fieldOf(input),
    kind = input.dataset.rule!,
    // eslint-disable-next-line @typescript-eslint/unbound-method -- called with its owner (fetch on window, a rule of makeRules that does not use this)
    fn = rules?.[RULE[kind]] as RuleFn | undefined;
  if (!field || !fn) return true;
  const empty = !input.value.trim();
  if (empty && !input.required) {
    setMsg(field, null);
    input.removeAttribute('aria-invalid');
    bankOut(input, null);
    return true;
  }
  const today = input.dataset.noFuture !== undefined ? jdnToIso(TODAY) : undefined;
  const r =
    kind === 'jalali-date'
      ? fn(input.value, today)
      : kind === 'money'
        ? fn(input.value, +(input.dataset.min || 10000), +(input.dataset.max || 50000000000))
        : fn(input.value);
  bankOut(input, r.bank);
  if (r.code) {
    if (show) {
      setMsg(field, 'err', icon('alert') + esc(messages[r.code] || r.code));
      input.setAttribute('aria-invalid', 'true');
    }
    return false;
  }
  input.removeAttribute('aria-invalid');
  const b = r.bank && window.dsBanks?.find(x => x.key === r.bank);
  const note = b
    ? BANK_OK[kind] + b.name
    : kind === 'money' && input.dataset.words !== undefined
      ? words(+r.value!) + ' ریال'
      : null;
  setMsg(field, note ? 'ok' : null, note ? icon('check-circle') + esc(note) : null);
  return true;
}
function bankOut(input: HTMLInputElement, key: string | null): void {
  const out = input.closest('.input-group')?.querySelector('[data-bank-out]');
  if (!out) return;
  const b = key && window.dsBanks?.find(x => x.key === key);
  out.innerHTML = b
    ? `<span class="ds-bank" title="بانک ${esc(b.name)}"><img src="${esc(bankMark(b.key))}" alt="بانک ${esc(b.name)}"></span>`
    : '';
}
export function initValidation(): void {
  const inputs = $$<HTMLInputElement>('input[data-rule]');
  if (!inputs.length) return;
  loadRules()
    .then(() =>
      inputs.forEach(i => {
        if (i.value) check(i, false);
      }),
    )
    .catch(() => {});
  inputs.forEach(input => {
    const kind = input.dataset.rule!;
    let touched = false;
    input.addEventListener('input', () => {
      if (FORMAT[kind]) {
        const before = input.value.length,
          pos = input.selectionStart ?? 0;
        input.value = FORMAT[kind](input.value);
        const p = Math.max(0, pos + input.value.length - before);
        try {
          input.setSelectionRange(p, p);
        } catch {}
      }
      if (kind === 'password') strength(input);
      check(input, touched);
      const confirmOf = document.querySelector<HTMLInputElement>(`[data-confirm-of="${input.id}"]`);
      if (confirmOf?.value) confirmCheck(confirmOf, true);
    });
    input.addEventListener('blur', () => {
      if (input.value) touched = true;
      check(input, touched);
    });
  });
  $$<HTMLInputElement>('input[data-confirm-of]').forEach(i => {
    i.addEventListener('input', () => confirmCheck(i, false));
    i.addEventListener('blur', () => confirmCheck(i, true));
  });
}
function confirmCheck(input: HTMLInputElement, show: boolean): boolean {
  const other = document.getElementById(input.dataset.confirmOf!) as HTMLInputElement | null,
    field = fieldOf(input);
  if (!other || !field) return true;
  const ok = input.value === other.value;
  if (!ok && show)
    setMsg(field, 'err', icon('alert') + esc(messages['password.mismatch'] || 'دو رمز با هم یکی نیستند.'));
  else if (ok && input.value) setMsg(field, 'ok', icon('check-circle') + 'دو رمز یکی است');
  else setMsg(field, null);
  return ok;
}
function strength(input: HTMLInputElement): void {
  const parts = passwordParts(input.value),
    meter = fieldOf(input)?.querySelector<HTMLElement>('.ds-strength');
  if (meter) (meter.dataset as Record<string, unknown>).s = parts.filter(Boolean).length;
  $$('[data-rule-part]', fieldOf(input)!).forEach((li, i) => li.classList.toggle('ok', parts[i]));
}
// T-171: root = one step of a form definition; the «new row» part of a rows field is checked by its add button, not here
export function validateForm(form: HTMLFormElement, root: ParentNode = form): boolean {
  if (!rules) return true; // rules not loaded: the server still checks
  const bad = $$<HTMLInputElement>('input[data-rule]', root).filter(
    i => !i.closest('.ds-rows-draft') && !check(i, true),
  );
  $$<HTMLInputElement>('input[data-confirm-of]', form).forEach(i => {
    if (!confirmCheck(i, true)) bad.push(i);
  });
  const sum = form.querySelector<HTMLElement>('.ds-err-summary');
  if (!bad.length) {
    if (sum) sum.hidden = true;
    return true;
  }
  if (sum) {
    sum.querySelector('ul')!.innerHTML = bad
      .map(
        i =>
          `<li><a href="#${i.id}" data-go="${i.id}">${esc((i.labels?.[0]?.textContent || '').replace('*', '').trim())}: ${esc(fieldOf(i)?.querySelector('.ds-msg')?.textContent.trim() || '')}</a></li>`,
      )
      .join('');
    sum.hidden = false;
    sum.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
    sum.addEventListener(
      'click',
      e => {
        const a = (e.target as Element).closest<HTMLElement>('[data-go]');
        if (a) {
          e.preventDefault();
          document.getElementById(a.dataset.go!)?.focus();
        }
      },
      { once: true },
    );
  } else bad[0].focus();
  return false;
}
// amount in words (Persian)
const ONES = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'],
  TEENS = ['ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده'];
const TENS = ['', '', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'],
  HUND = ['', 'صد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد'];
const SCALE = ['', 'هزار', 'میلیون', 'میلیارد', 'هزار میلیارد'];
function three(n: number): string {
  const h = Math.floor(n / 100),
    t = Math.floor((n % 100) / 10),
    o = n % 10,
    out: string[] = [];
  if (h) out.push(HUND[h]);
  if (t === 1) out.push(TEENS[o]);
  else {
    if (t) out.push(TENS[t]);
    if (o) out.push(ONES[o]);
  }
  return out.join(' و ');
}
export function words(n: number): string {
  if (!n) return 'صفر';
  const parts: string[] = [];
  let s = 0;
  while (n > 0 && s < SCALE.length) {
    const g = n % 1000;
    if (g) parts.unshift(three(g) + (SCALE[s] ? ' ' + SCALE[s] : ''));
    n = Math.floor(n / 1000);
    s++;
  }
  return parts.join(' و ');
}
